import type {
  FacilityServiceFieldSetting,
  FieldSetting,
  FieldSettings,
} from "../models/facilitySettings";

export const parseSettings = (fieldSettings: FacilityServiceFieldSetting[]) => {
  const result: FieldSettings = {};

  for (let ind = 0, len = fieldSettings.length; ind < len; ind++) {
    const setting = fieldSettings[ind];
    const readOnly = !!setting.State.find((value) => value === "Readonly");
    const hidden = !!setting.State.find((value) => value === "Hidden");
    const required = !!setting.State.find((value) => value === "Required");

    result[setting.Field] = {
      readOnly,
      hidden,
      required,
    };
  }

  return result;
};

export const isReadOnlyField = (settings: FieldSettings, field: string) => {
  const fieldSetting = settings[field];
  if (fieldSetting?.readOnly) {
    return true;
  }

  return false;
};

export const isHiddenField = (settings: FieldSettings, field: string): boolean =>
  !!settings[field]?.hidden;

export const isRequiredField = (settings: FieldSettings, field: string): boolean =>
  !!settings[field]?.required;

const columnToSettingsMapping: {
  codeGridColumnsIds: string[];
  columnToSetting: Record<string, string | { settingField: string; opGrouperType: string }>;
}[] = [
  // Some setting fields are missing and need to be checked again after the facility field settings are released.
  {
    codeGridColumnsIds: ["diagnosesPdxOn", "diagnosesPdxOff"],
    columnToSetting: {
      colPresentOnAdmission: "Diagnoses.PresentOnAdmission",
      colDescription: "Diagnoses.Description",
      colHcc24: "HCCsV24",
      colHcc28: "HCCsV28",
    },
  },
  {
    codeGridColumnsIds: ["diagPsych"],
    columnToSetting: {
      colPresentOnAdmission: "Diagnoses.PresentOnAdmission",
      colDescription: "Diagnoses.Description",
      colHcc24: "HCCsV24",
      colHcc28: "HCCsV28",
      colComorbidity: "Diagnoses.Comorbidity",
    },
  },
  {
    codeGridColumnsIds: ["visitReasons"],
    columnToSetting: {
      colDescription: "VisitReasons.Description",
      colHcc24: "HCCsV24",
      colHcc28: "HCCsV28",
    },
  },
  {
    codeGridColumnsIds: ["inProcedures"],
    columnToSetting: {
      colDescription: "InProcedures.Description",
      colDate: "InProcedures.ProcedureDate",
      colEpisode: "InProcedures.Episode",
      colProvider: "InProcedures.Provider",
    },
  },
  {
    codeGridColumnsIds: ["outProcedures"],
    columnToSetting: {
      colDate: "OutProcedures.ProcedureDate",
      colEpisode: "OutProcedures.Episode",
      colProvider: "OutProcedures.Provider",
      colDescription: "OutProcedures.Description",
      colModifiers: "OutProcedures.Modifiers",
      colCharges: "OutProcedures.Charges",
      colNCCharges: "OutProcedures.NonCoveredCharges",
      colRevenueCode: "OutProcedures.RevenueCode",
      colUnits: "OutProcedures.UnitsOfService",
      colApc: {
        settingField: "OutProcedures.APC",
        opGrouperType: "OCEAPC",
      },
      colApcDescription: {
        settingField: "OutProcedures.APCDescription",
        opGrouperType: "OCEAPC",
      },
      colApcWeight: {
        settingField: "OutProcedures.APCWeight",
        opGrouperType: "OCEAPC",
      },
      colApcStatusIndicator: {
        settingField: "OutProcedures.APCStatusIndicator",
        opGrouperType: "OCEAPC",
      },
      colApcCaFlag: {
        settingField: "OutProcedures.APCCompAdjFlag",
        opGrouperType: "OCEAPC",
      },
      colApcPercent: {
        settingField: "OutProcedures.APCPercentage",
        opGrouperType: "OCEAPC",
      },
      colApcMedicareAmount: {
        settingField: "OutProcedures.APCMedicareAmount",
        opGrouperType: "OCEAPC",
      },
      colApcAdjustedCoin: {
        settingField: "OutProcedures.APCAdjustedCoin",
        opGrouperType: "OCEAPC",
      },
      colApcOutlierAmount: {
        settingField: "OutProcedures.APCOutlierAmt",
        opGrouperType: "OCEAPC",
      },
      colApcPayment: {
        settingField: "OutProcedures.APCPayment",
        opGrouperType: "OCEAPC",
      },

      colEAPG: {
        settingField: "OutProcedures.EAPG",
        opGrouperType: "EAPG",
      },
      colEAPGCategory: {
        settingField: "OutProcedures.EAPGCategory",
        opGrouperType: "EAPG",
      },
      colEAPGType: {
        settingField: "OutProcedures.EAPGType",
        opGrouperType: "EAPG",
      },
      colEAPGPayment: {
        settingField: "OutProcedures.EAPGPayment",
        opGrouperType: "EAPG",
      },
      colEAPGPricingStatus: {
        settingField: "OutProcedures.EAPGPricingStatus",
        opGrouperType: "EAPG",
      },
      colEAPGDescription: {
        settingField: "OutProcedures.EAPGDescription",
        opGrouperType: "EAPG",
      },
      colEAPGWeight: {
        settingField: "OutProcedures.EAPGWeight",
        opGrouperType: "EAPG",
      },
      colEAPGPercentage: {
        settingField: "OutProcedures.EAPGPercentage",
        opGrouperType: "EAPG",
      },

      colASCPayment: {
        settingField: "OutProcedures.ASCPayment",
        opGrouperType: "ASC",
      },
      colASCWeight: {
        settingField: "OutProcedures.ASCWeight",
        opGrouperType: "ASC",
      },
      colASCPercentage: {
        settingField: "OutProcedures.ASCPercentage",
        opGrouperType: "ASC",
      },
      colASCMedicareAmount: {
        settingField: "OutProcedures.ASCMedicareAmount",
        opGrouperType: "ASC",
      },
      colASCAdjustedCoin: {
        settingField: "OutProcedures.ASCAdjustedCoin",
        opGrouperType: "ASC",
      },
      colASCPaymentIndicator: {
        settingField: "OutProcedures.ASCPaymentIndicator",
        opGrouperType: "ASC",
      },
    },
  },
  {
    codeGridColumnsIds: ["professionalProcedures"],
    columnToSetting: {
      colDate: "ProProcedures.ProcedureDate",
      colDescription: "ProProcedures.Description",
      colModifiers: "ProProcedures.Modifiers",
      colDxLink1: "ProProcedures.DxLink1",
      colDxLink2: "ProProcedures.DxLink2",
      colDxLink3: "ProProcedures.DxLink3",
      colDxLink4: "ProProcedures.DxLink4",
      colPlaceOfService: "ProProcedures.PlaceOfService",
      colMalpracticeRVU: "ProProcedures.MalpracticeRVU",
      colFacilityPracExpRVU: "ProProcedures.FacilityPracticeExpenseRVU",
      colNonFacilityPracExpRVU: "ProProcedures.NonFacilityPracticeExpenseRVU",
      colEpisode: "ProProcedures.Episode",
      colUnits: "ProProcedures.UnitsOfService",
      colRevenueCode: "ProProcedures.RevenueCode",
      colProvider: "ProProcedures.Provider",
      colCharges: "ProProcedures.Charges",
    },
  },
];

export const getColumnFieldSetting = (
  settings: FieldSettings,
  codeGridColumnsId: string,
  columnId: string,
  opGrouperType?: string,
): FieldSetting => {
  const defaultSettings: FieldSetting = {
    hidden: false,
    readOnly: false,
    required: false,
  };

  const gridSettings = columnToSettingsMapping.find((item) =>
    item.codeGridColumnsIds.includes(codeGridColumnsId),
  );

  const mappingConfig = gridSettings?.columnToSetting[columnId];

  if (!mappingConfig) {
    return defaultSettings;
  }

  if (typeof mappingConfig === "string") {
    return settings[mappingConfig] || defaultSettings;
  }

  if (typeof mappingConfig === "object") {
    const columnSettings = { ...(settings[mappingConfig.settingField] || defaultSettings) };

    if (mappingConfig.opGrouperType && opGrouperType !== mappingConfig.opGrouperType) {
      columnSettings.hidden = true;
    }

    return columnSettings;
  }

  return defaultSettings;
};
